import { SuccessFilled } from '@element-plus/icons-vue'
import classNames from 'classnames'
import { getUserCenterPageRoute, UserCenterRoutes } from 'crypto-otc/constants/PageRoutes'
import router from 'crypto-otc/router'
import { ElForm, ElFormItem, ElIcon } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { CommonConfirmSecurityAuthType } from 'network/src/constants/security'
import { useUserInfoStore } from 'store'
import { DefineComponentProps } from 'utils'
import { ArrowRight } from '../icons/ArrowRight'
import { CustomButton, DialogHeader, HocElDialog, InputView, SecurityBottom, SizedBox, UiUtils, type SerializerPhone } from '../index'
import { AuthSource, LoginAuthSource, PasswordAuthSource, useSecurityAuthStore, WithdrawAuthSource, type NeedSecurityAuth } from '../stores/use-security-auth'
import './SecurityAuth.scss'

export type SecurityAuthProps<T = any> = {
  type: CommonConfirmSecurityAuthType
  authSource: AuthSource | PasswordAuthSource | WithdrawAuthSource | LoginAuthSource
  phone?: SerializerPhone
  teleport?: HTMLElement
  /** 临时token，用于登录时验证 */
  tempToken?: string
  onResolve?: (data?: T) => void | Promise<void>
  onReject?: () => void | Promise<void>
  onClose?: () => void
  /** 是否必要，如果必要，但是没有设置安全验证， 弹框，并阻塞下一步 */
  require?: boolean
  onGotoSetting?: () => void
}

type NoReceiveMessageItem = {
  subTitle?: string
  desc: string[]
}

export const SecurityAuth = defineComponent<SecurityAuthProps>({
  props: DefineComponentProps<SecurityAuthProps>(['type', 'authSource', 'teleport', 'tempToken', 'onResolve', 'onReject', 'onClose', 'phone', 'require', 'onGotoSetting']),
  setup(props) {
    const { t } = useI18n()
    const securityAuth = useSecurityAuthStore()
    const userInfoStore = useUserInfoStore()
    const showAuthGoogle = ref(false)
    const showAuthEmail = ref(false)
    const showAuthMobile = ref(false)

    const showSecurityAuthDialog = ref(false)
    const settingAuthDialog = ref(false)
    const showNoCodeDialog = ref(false)
    const preAuthSource = ref<AuthSource>()

    const submitAuth = () => {
      securityAuth.submitAuth()
    }

    const backToAuthIndex = () => {
      showAuthGoogle.value = false
      showAuthEmail.value = false
      showAuthMobile.value = false

      if (!securityAuth.isAllAuthCompleted.value) {
        showSecurityAuthDialog.value = true
      } else {
        onResolve(securityAuth.lastVerifyResult.value)
      }
      securityAuth.googleCode.value = ''
      securityAuth.emailCode.value = ''
      securityAuth.mobileCode.value = ''
    }

    watchEffect(() => {
      if (props.tempToken) {
        securityAuth.tempToken.value = props.tempToken
      }
    })

    const setSecurityAuthSource = (item: NeedSecurityAuth) => {
      if (item.isCompleted) return
      const source = item.type
      showSecurityAuthDialog.value = false
      if (source === AuthSource.Google) {
        showAuthGoogle.value = true
      }
      if (source === AuthSource.Email) {
        showAuthEmail.value = true
      }
      if (source === AuthSource.Mobile) {
        showAuthMobile.value = true
      }
    }

    const onResolve = async (data?: any) => {
      await props.onResolve?.(data)
      setTimeout(() => {
        props.onClose?.()
      }, 300)
    }

    const onReject = async () => {
      await props.onReject?.()
      setTimeout(() => {
        props.onClose?.()
      }, 300)
    }

    const onStartSecurityAuth = async () => {
      if (props.authSource === WithdrawAuthSource.Withdraw && !userInfoStore.isSetTradePassword) {
        UiUtils.showModal(t('请先设置支付密码'))
        return onReject()
      }

      if (!userInfoStore.isOpenSecurity) {
        if (props.require) {
          settingAuthDialog.value = true
          return
        } else {
          return onResolve()
        }
      }
      securityAuth.currentAuthSource.value = props.authSource
      securityAuth.setType(props.type)
      if (securityAuth.isAllAuthCompleted.value || securityAuth.needSecurityAuthMap.value.size === 0) {
        return onResolve()
      }
      showSecurityAuthDialog.value = true
    }

    onMounted(() => {
      onStartSecurityAuth()
    })

    const authGoogle = async () => {
      const isSuccess = await securityAuth.authGoogle()
      if (isSuccess) {
        nextTick(backToAuthIndex)
      }
    }

    const authEmail = async () => {
      const isSuccess = await securityAuth.authEmail()
      if (isSuccess) {
        backToAuthIndex()
      }
    }

    const authMobile = async () => {
      const isSuccess = await securityAuth.authMobile(props.phone)
      if (isSuccess) {
        backToAuthIndex()
      }
    }

    const autoAuthGoogle = (code: string) => {
      if (code.length !== 6) return
      authGoogle()
    }

    const autoAuthEmail = (code: string) => {
      if (code.length !== 6) return
      authEmail()
    }

    const autoAuthMobile = (code: string) => {
      if (code.length !== 6) return
      authMobile()
    }

    const closeSecurityAuthDialog = () => {
      showSecurityAuthDialog.value = false
      onReject()
    }

    const teleport = computed(() => {
      return props.teleport || document.body
    })

    const preventDefaultForms = {
      onSubmit: (e: Event) => {
        e.preventDefault()
      },
    }

    const openNoCodeDialog = (source: AuthSource) => {
      preAuthSource.value = source

      if (source === AuthSource.Google) {
        showAuthGoogle.value = false
      }
      if (source === AuthSource.Email) {
        showAuthEmail.value = false
      }
      if (source === AuthSource.Mobile) {
        showAuthMobile.value = false
      }
      showNoCodeDialog.value = true
    }

    const notReceiveMessage = computed<NoReceiveMessageItem>(() => {
      if (preAuthSource.value === AuthSource.Google) {
        return {
          subTitle: t('不妨试试以下步骤?'),
          desc: [t('请确认您是否填入了正确的验证码。'), t('请确认验证码是否过期。'), t('请确认当前手机时区是否配置正确。'), t('请确保您的App已是最新版本。')],
        }
      }
      if (preAuthSource.value === AuthSource.Mobile) {
        return {
          subTitle: t('不妨试试以下步骤?'),
          desc: [t('请确保您输入的手机号正确无误。'), t('请稍等片刻,可能会有延迟。'), t('如果您始终没收到验证码,请联系我们的客服。')],
        }
      }
      if (preAuthSource.value === AuthSource.Email) {
        return {
          subTitle: t('不妨试试以下步骤?'),
          desc: [t('请确保您输入的邮箱正确无误。'), t('请检查您的垃圾邮箱,您可能会找到想的邮件。'), t('请稍等片刻,可能会有延迟。'), t('如果您始终没收到验证码,请联系我们的客服。')],
        }
      }
      return {
        desc: [],
      }
    })

    const ToggleAuthType = (props: { source: AuthSource }) => {
      return (
        <>
          {securityAuth.minVerifyNum.value < securityAuth.needSecurityAuthMap.value.size && (
            <div class="text-button" onClick={backToAuthIndex}>
              {t('切换验证方式')}
            </div>
          )}
          <div class="text-button" onClick={() => openNoCodeDialog(props.source)}>
            {t('没有收到验证码')}
          </div>
        </>
      )
    }

    const onResetAuthDialog = () => {
      showNoCodeDialog.value = false
      if (preAuthSource.value === AuthSource.Google) {
        showAuthGoogle.value = true
      }
      if (preAuthSource.value === AuthSource.Email) {
        showAuthEmail.value = true
      }
      if (preAuthSource.value === AuthSource.Mobile) {
        showAuthMobile.value = true
      }
    }
    const handleGotoSetting = () => {
      settingAuthDialog.value = false
      props.onGotoSetting?.()
      router.push({
        path: getUserCenterPageRoute(UserCenterRoutes.SecuritySettings),
      })
    }
    return () => {
      return (
        <div class="security-auth-layout">
          <>
            <HocElDialog class="security-auth-dialog" v-model={settingAuthDialog.value} width={430} showClose={false} closeOnClickModal={false} appendTo={teleport.value}>
              {{
                header: () => {
                  return <DialogHeader onClose={closeSecurityAuthDialog}>{t('安全验证')}</DialogHeader>
                },
                default: () => (
                  <div class="security-auth-content-layout">
                    <div class="security-auth-content">
                      <div class={'setting-content'}>{t('为保障您的资金安全，请先在个人中心->安全设置中绑定谷歌、手机或邮箱，然后设置支付密码')}</div>
                      <div class={'setting-action'}>
                        <CustomButton type={'aux'} class={'security-auth-btn step-index-auth-btn'} onClick={() => (settingAuthDialog.value = false)}>
                          {t('取消')}
                        </CustomButton>
                        <CustomButton type={'primary'} class={'security-auth-btn step-index-auth-btn'} onClick={handleGotoSetting}>
                          {t('确定')}
                        </CustomButton>
                      </div>
                    </div>
                  </div>
                ),
              }}
            </HocElDialog>
            <HocElDialog
              class="security-auth-dialog"
              v-model={showSecurityAuthDialog.value}
              width={430}
              showClose={false}
              offsetTop={64}
              closeOnClickModal={false}
              appendTo={teleport.value}
            >
              {{
                header: () => {
                  return <DialogHeader onClose={closeSecurityAuthDialog}>{t('安全验证要求')}</DialogHeader>
                },
                default: () => (
                  <div class="security-auth-content-layout">
                    <div class="security-auth-content">
                      <div class="auth-step-tip">{t('你需要完成以下验证方可继续')}</div>
                      <div class="auth-step">
                        <span class={'current'}>{securityAuth.completedAuthNum.value}</span>
                        <span class={'total'}>/{securityAuth.minVerifyNum.value}</span>
                      </div>

                      <div class="security-auth-list">
                        {Array.from(securityAuth.needSecurityAuthMap.value.values()).map((item) => {
                          const IconComp = securityAuth.getAuthImage(item.type)
                          return (
                            <div
                              class={classNames('security-auth-item', {
                                disabled: item.isCompleted,
                              })}
                              onClick={() => setSecurityAuthSource(item)}
                              key={item.type}
                            >
                              {IconComp && <IconComp />}
                              <div class="security-auth-item-title">{securityAuth.getAuthTitle(item.type)}</div>
                              <div class="right-icon">
                                {item.isCompleted ? (
                                  <ElIcon class={'success-icon'} color="var(--colors-up)">
                                    <SuccessFilled />
                                  </ElIcon>
                                ) : (
                                  <ArrowRight class={'arrow-right'} />
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      <CustomButton type={'primary'} class={'security-auth-btn step-index-auth-btn'} onClick={submitAuth}>
                        {t('确定')}
                      </CustomButton>

                      <SecurityBottom />
                    </div>
                  </div>
                ),
              }}
            </HocElDialog>

            <HocElDialog
              class="security-auth-dialog"
              v-model={showAuthGoogle.value}
              offsetTop={64}
              width={430}
              closeOnClickModal={false}
              showClose={false}
              appendTo={teleport.value}
            >
              {{
                header: () => {
                  return <DialogHeader onClose={backToAuthIndex}>{t('安全验证')}</DialogHeader>
                },
                default: () => (
                  <div class="security-auth-content-layout">
                    <div class="security-auth-content">
                      <ElForm labelPosition="top" {...preventDefaultForms}>
                        <ElFormItem label={t('谷歌验证码')}>
                          <InputView v-model={securityAuth.googleCode.value} placeholder={t('请输入谷歌验证码')} maxlength={6} isNumber onInput={autoAuthGoogle} />
                          <div class="auth-step-tip">{t('请输入验证器App生成的6位验证码')}</div>
                        </ElFormItem>
                      </ElForm>

                      <CustomButton
                        type={'primary'}
                        class={'security-auth-btn'}
                        disabled={securityAuth.googleCode.value.length !== 6}
                        onClick={authGoogle}
                        loading={securityAuth.loadingAuthGoogle.value}
                      >
                        {t('确定')}
                      </CustomButton>
                      <ToggleAuthType source={AuthSource.Google} />

                      <SecurityBottom />
                    </div>
                  </div>
                ),
              }}
            </HocElDialog>

            <HocElDialog
              class="security-auth-dialog"
              v-model={showAuthEmail.value}
              offsetTop={64}
              width={430}
              closeOnClickModal={false}
              showClose={false}
              appendTo={teleport.value}
            >
              {{
                header: () => {
                  return <DialogHeader onClose={backToAuthIndex}>{t('安全验证')}</DialogHeader>
                },
                default: () => (
                  <div class="security-auth-content-layout">
                    <div class="security-auth-content">
                      <div class="auth-send-tip">
                        {t('邮箱验证码将发送至')}
                        <span class="send-address">{userInfoStore.userInfo?.accountInfo.email}</span>
                      </div>
                      <ElForm labelPosition="top" {...preventDefaultForms}>
                        <ElFormItem label={t('邮箱验证码')}>
                          <InputView
                            v-model={securityAuth.emailCode.value}
                            placeholder={t('请输入邮箱验证码')}
                            maxlength={6}
                            isNumber
                            onInput={autoAuthEmail}
                            suffixIcon={
                              <span
                                class={classNames('get-code-btn', {
                                  disabled: securityAuth.emailCountDown.value >= 0,
                                })}
                                onClick={securityAuth.sendVerifyEmailCode}
                              >
                                {securityAuth.emailCountDown.value > 0 ? `${securityAuth.emailCountDown.value}s` : t('获取验证码')}
                              </span>
                            }
                          />
                          <div class="auth-step-tip">{t('请输入6位邮箱验证码')}</div>
                        </ElFormItem>
                      </ElForm>

                      <CustomButton
                        type={'primary'}
                        class={'security-auth-btn'}
                        disabled={securityAuth.emailCode.value.length !== 6}
                        onClick={authEmail}
                        loading={securityAuth.loadingAuthEmail.value}
                      >
                        {t('确定')}
                      </CustomButton>
                      <ToggleAuthType source={AuthSource.Email} />

                      <SecurityBottom />
                    </div>
                  </div>
                ),
              }}
            </HocElDialog>

            <HocElDialog
              class="security-auth-dialog"
              v-model={showAuthMobile.value}
              offsetTop={64}
              width={430}
              closeOnClickModal={false}
              showClose={false}
              appendTo={teleport.value}
            >
              {{
                header: () => {
                  return <DialogHeader onClose={backToAuthIndex}>{t('安全验证')}</DialogHeader>
                },
                default: () => (
                  <div class="security-auth-content-layout">
                    <div class="security-auth-content">
                      <div class="auth-send-tip">
                        {t('手机验证码将发送至')}
                        <span class="send-address">
                          {userInfoStore.currentArea}&nbsp;
                          {userInfoStore.userInfo?.accountInfo.mobile}
                        </span>
                      </div>
                      <ElForm labelPosition="top" {...preventDefaultForms}>
                        <ElFormItem label={t('手机验证码')}>
                          <InputView
                            v-model={securityAuth.mobileCode.value}
                            placeholder={t('请输入手机验证码')}
                            maxlength={6}
                            isNumber
                            onInput={autoAuthMobile}
                            suffixIcon={
                              <span
                                class={classNames('get-code-btn', {
                                  disabled: securityAuth.mobileCountDown.value >= 0,
                                })}
                                onClick={() => securityAuth.sendVerifyMobileCode(props.phone)}
                              >
                                {securityAuth.mobileCountDown.value > 0 ? `${securityAuth.mobileCountDown.value}s` : t('获取验证码')}
                              </span>
                            }
                          />
                          <div class="auth-step-tip">{t('请输入6位手机验证码')}</div>
                        </ElFormItem>
                      </ElForm>

                      <CustomButton
                        type={'primary'}
                        class={'security-auth-btn'}
                        disabled={securityAuth.mobileCode.value.length !== 6}
                        onClick={authMobile}
                        loading={securityAuth.loadingAuthMobile.value}
                      >
                        {t('确定')}
                      </CustomButton>
                      <ToggleAuthType source={AuthSource.Mobile} />

                      <SecurityBottom />
                    </div>
                  </div>
                ),
              }}
            </HocElDialog>

            <HocElDialog
              class="security-auth-dialog"
              v-model={showNoCodeDialog.value}
              offsetTop={64}
              width={430}
              closeOnClickModal={false}
              showClose={false}
              appendTo={teleport.value}
            >
              {{
                header: () => {
                  return <DialogHeader onClose={onResetAuthDialog}>{t('没有收到验证码？')}</DialogHeader>
                },
                default: () => {
                  return (
                    <div class="no-receive-message-layout no-code-dialog-content">
                      {notReceiveMessage.value.subTitle && <div class={'no-code-dialog-sub-title'}>{notReceiveMessage.value.subTitle}</div>}
                      <div class={'no-code-dialog-message'}>
                        {notReceiveMessage.value.desc.map((item, index) => {
                          return (
                            <div class={'no-code-dialog-message-item'} key={index}>
                              {index + 1}.&nbsp;
                              {item}
                            </div>
                          )
                        })}
                      </div>
                      <SizedBox height={80} />
                      <CustomButton type={'primary'} class={'to-auth-btn'} onClick={onResetAuthDialog}>
                        {t('返回验证')}
                      </CustomButton>
                    </div>
                  )
                },
              }}
            </HocElDialog>
          </>
        </div>
      )
    }
  },
})
